import {
  Boundary,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  useFloating,
  useMergeRefs,
} from '@floating-ui/react'
import { Command as CommandPrimitive } from 'cmdk'
import { Check, ChevronDown, X } from 'lucide-react'
import {
  ComponentPropsWithoutRef,
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  Ref,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandItem, CommandList } from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { getInputContainerClasses } from '~/@/lib/style-utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { EnumChip } from '~/components/shared/EnumChip'
import { EnumOptionMark } from '~/components/shared/EnumOptionMark'
import { InputWrapper } from '../base/input-wrapper'
import { BaseInputProps } from '../base/types'
import { Option } from './utils'

// Base props shared by both single and multi-select modes
interface BaseDropdownProps<T extends string = string> extends BaseInputProps {
  options: Option<T>[]
  placeholder?: string
  emptyIndicator?: ReactNode
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  disabled?: boolean
  className?: string
  inputProps?: Omit<
    ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
    'value' | 'placeholder' | 'disabled'
  >
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  /** Show the clear button to clear all selections on click */
  showClearButton?: boolean
  /** Prevent input and chips from wrapping to additional lines and enable horizontal scrolling. */
  horizontalScroll?: boolean
  /** Allow user to create option when there is no option matched. */
  creatable?: boolean
  /** Hide the create item command item when creating option when there is no option matched */
  hideCreateItem?: boolean
  type?: 'text' | 'number'
  /** Maximum number of items that can be selected */
  maxSelected?: number
  /** Callback when the maximum number of items is selected */
  onMaxSelected?: (maxLimit: number) => void
  /** Hide the placeholder when there are items selected */
  showPlaceholderWhenSelected?: boolean
  /**
  Optional boundary for the dropdown's menu. By default, this is simply the viewport.
  The boundary determines where the menu will flip to stay within
  */
  customBoundary?: Boundary | null
  /** Render dropdown menu in a portal. Useful when dropdown is used inside DataGrid or other components with transform properties. */
  usePortal?: boolean
  /** Disable filtering by user entered text substring. Defaults to false. */
  isAutocompleteDisabled?: boolean
  /**
   * Blur the input after committing a single selection, collapsing the control. Defaults to
   * false, which keeps focus on the input (the accessible combobox default). No effect in multi-select.
   */
  blurOnSelect?: boolean
}

// Single select mode
interface SingleSelectDropdownProps<T extends string = string> extends BaseDropdownProps<T> {
  isMultiSelect?: false
  value?: T | null
  onChange?: (value: T | undefined) => void
}

// Multi-select mode
interface MultiSelectDropdownProps<T extends string = string> extends BaseDropdownProps<T> {
  isMultiSelect: true
  values: T[]
  onChange?: (values: T[] | undefined) => void
}

type DropdownProps<T extends string = string> =
  | SingleSelectDropdownProps<T>
  | MultiSelectDropdownProps<T>

/**
 * Dropdown component for single and multi-select with autocomplete.
 *
 * Supports:
 *  - Single-select (isMultiSelect: false or omitted)
 *  - Multi-select (isMultiSelect: true)
 *  - Creatable options (user can add new entries when enabled)
 *
 * Generic on string values (option values are always strings).
 */
function DropdownComponent<T extends string = string>(
  props: DropdownProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const {
    onBlur,
    placeholder,
    options = [],
    emptyIndicator,
    startAdornment,
    endAdornment,
    disabled,
    className,
    wrapperClassName,
    errorText,
    helperText,
    label,
    required,
    showDirtyIndicator,
    tooltip,
    isBorderless = false,
    showClearButton = true,
    isMultiSelect = false,
    horizontalScroll = false,
    type = 'text',
    creatable = false,
    hideCreateItem = false,
    maxSelected = Number.MAX_SAFE_INTEGER,
    onMaxSelected,
    showPlaceholderWhenSelected = false,
    usePortal = false,
    inputProps,
    customBoundary,
    readOnly,
    isAutocompleteDisabled = false,
    blurOnSelect = false,
  } = props

  // Extract mode-specific props via discriminant narrowing on props — no casts needed.
  // Checking props.isMultiSelect (not the destructured bool) lets TypeScript narrow props.
  const values = props.isMultiSelect
    ? props.values
    : props.value !== undefined
      ? [props.value]
      : undefined

  // Narrowed onChange callbacks — TypeScript infers the correct signature for each mode
  const onChangeSingle = !props.isMultiSelect ? props.onChange : undefined
  const onChangeMulti = props.isMultiSelect ? props.onChange : undefined

  const optionValues = useMemo(() => {
    return values
      ? values
          .map((value) => {
            // First try to find the value in the options array
            const existingOption = options.find((option) => option.value === value)
            // If not found and creatable, create a new option
            if (!existingOption && creatable) {
              return { value, label: value }
            }
            if (!existingOption && !creatable) {
              return undefined
            }
            return existingOption
          })
          .filter((option): option is Option<T> => option !== undefined)
      : []
  }, [values, options, creatable])
  // Reserve the leading gutter on every row when any option has an icon
  const someOptionHasIcon = useMemo(() => options.some((option) => !!option.iconName), [options])
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(isMultiSelect ? '' : optionValues[0]?.label || '')
  const { t } = useTypedTranslation()

  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: 'fixed',
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(),
      flip({
        ...(customBoundary && { boundary: customBoundary }),
        fallbackPlacements: ['top', 'bottom'],
      }),
    ],
  })

  const anchorRef = useMergeRefs([refs.setReference, containerRef])
  const floatingRef = useMergeRefs([refs.setFloating, listRef])

  // Update input value when the selected value changes
  useEffect(() => {
    if (isMultiSelect) {
      setInputValue('')
    } else if (optionValues.length > 0) {
      // Set the input value to the selected option's label
      setInputValue(optionValues[0]?.label || '')
    } else if (!isOpen || isAutocompleteDisabled) {
      // Clear input when no value is selected AND dropdown is closed or autocomplete is disabled
      // When dropdown is open, user might be typing to filter, so preserve inputValue
      setInputValue('')
    }
  }, [isMultiSelect, optionValues, isOpen, isAutocompleteDisabled])

  // Restores the displayed input text to match the committed selection.
  // Called when closing the dropdown without making a new selection (Escape, blur).
  // Single-select: shows the selected option's label, or empty if nothing is selected.
  // Multi-select: always empty — the chips already display the selection.
  const syncInputToSelection = () => {
    setInputValue(isMultiSelect ? '' : optionValues[0]?.label || '')
  }

  // Clears the current selection by firing onChange with undefined.
  const clearSelection = () => {
    if (isMultiSelect) {
      onChangeMulti?.(undefined)
    } else {
      onChangeSingle?.(undefined)
    }
  }

  const handleUnselect = (option: Option<T>) => {
    const newValues = optionValues.filter((value) => value.value !== option.value)
    if (isMultiSelect) {
      onChangeMulti?.(newValues.map((value) => value.value))
    } else {
      onChangeSingle?.(newValues[0]?.value)
    }
  }

  const handleValueChange = (newValue: string) => {
    // If autocomplete is disabled, don't allow typing to change the value
    if (isAutocompleteDisabled) {
      return
    }

    // If there is a selected value, do not let the user type in the input
    // But allow clearing/editing if the value is being modified
    if (optionValues.length > 0 && !isMultiSelect) {
      const selectedLabel = optionValues[0]?.label || ''
      if (newValue === selectedLabel || newValue.length >= selectedLabel.length) {
        return
      }
      if (newValue.length < selectedLabel.length) {
        onChangeSingle?.(undefined)
      }
    }

    setInputValue(newValue)
    // If the input is cleared, also clear the selection
    if (newValue === '') {
      clearSelection()
    }
    setIsOpen(true) // Always open dropdown when typing
    inputProps?.onValueChange?.(newValue)
  }

  const handleCreateItem = (value: string) => {
    if (optionValues.length >= maxSelected) {
      onMaxSelected?.(optionValues.length)
      return
    }
    // Clear the search text. The useEffect syncing optionValues → inputValue
    // will restore the selected label on the next render for single-select.
    setInputValue('')
    const newOptions = [...optionValues, { value: value as T, label: value }]
    if (isMultiSelect) {
      onChangeMulti?.(newOptions.map((o) => o.value))
    } else {
      onChangeSingle?.(value as T)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault()
      setIsOpen(true)
      return
    }

    if (e.key === 'Escape' && isOpen) {
      e.preventDefault()
      setIsOpen(false)
      syncInputToSelection()
      return
    }

    // If autocomplete is disabled, prevent backspace from clearing selection
    if (isAutocompleteDisabled && e.key === 'Backspace') {
      e.preventDefault()
      return
    }

    // In multi-select, allow backspace to remove the last selected item when input is empty
    if (e.key === 'Backspace' && isMultiSelect) {
      if (inputValue === '' && optionValues.length > 0) {
        handleUnselect(optionValues[optionValues.length - 1])
      }
      return
    }

    // In single-select, allow backspace to clear selection when input matches selected value
    if (e.key === 'Backspace' && !isMultiSelect && optionValues.length > 0) {
      const selectedLabel = optionValues[0]?.label || ''
      // If input matches the selected value exactly, clear selection on backspace
      if (inputValue === selectedLabel) {
        e.preventDefault()
        clearSelection()
        setInputValue('')
        return
      }
      // If input is shorter than selected value, allow normal backspace behavior
      // The handleValueChange will handle clearing the selection
    }

    if (e.key === 'Enter' && inputValue.trim() !== '' && creatable) {
      e.preventDefault()
      const trimmedValue = inputValue.trim()
      if (isValidValue(trimmedValue)) {
        handleCreateItem(trimmedValue)
      }
    }
  }

  // Prevent ancestor menus from intercepting printable input as type-ahead navigation.
  const handleKeyDownCapture = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.stopPropagation()
    }
  }

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (readOnly) {
      return
    }
    setIsOpen(true)
    if (inputRef.current) {
      inputRef.current.select()
    }
    inputProps?.onFocus?.(e)
  }

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
      // Scroll to the end to show the input
      if (scrollContainerRef.current && horizontalScroll) {
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
              left: scrollContainerRef.current.scrollWidth,
              behavior: 'smooth',
            })
          }
        })
      }
    }
  }, [isOpen, optionValues, horizontalScroll])

  // Keep input in view when typing
  useEffect(() => {
    if (isOpen && horizontalScroll && scrollContainerRef.current) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            left: scrollContainerRef.current.scrollWidth,
            behavior: 'smooth',
          })
        }
      })
    }
  }, [inputValue, isOpen, horizontalScroll])

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const focusMovedWithinContainer =
      e.relatedTarget instanceof Node && containerRef.current?.contains(e.relatedTarget)
    if (focusMovedWithinContainer) {
      return
    }

    syncInputToSelection()
    setIsOpen(false)
    // Scroll back to the start when multi-select closes
    if (isMultiSelect && scrollContainerRef.current && horizontalScroll) {
      scrollContainerRef.current.scrollTo({ left: 0, behavior: 'smooth' })
    }
    onBlur?.(e)
    inputProps?.onBlur?.(e)
  }

  const isValidValue = (value: string): boolean => {
    if (type === 'number') {
      const numValue = Number(value)
      return !isNaN(numValue) && value.trim() !== ''
    }
    return true
  }

  const CreatableItem = () => {
    if (!creatable || hideCreateItem) {
      return undefined
    }

    return (
      <CommandItem
        className="cursor-pointer"
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onSelect={(value: string) => {
          if (optionValues.length >= maxSelected) {
            onMaxSelected?.(optionValues.length)
            return
          }

          if (!isValidValue(value)) {
            return
          }

          // Clear the search text. The useEffect syncing optionValues → inputValue
          // will restore the selected label on the next render for single-select.
          setInputValue('')
          const newOptions = [...optionValues, { value: value as T, label: value }]
          if (isMultiSelect) {
            onChangeMulti?.(newOptions.map((o) => o.value))
          } else {
            onChangeSingle?.(value as T)
          }
        }}
        value={inputValue}
      >
        <span
          className={cn('truncate overflow-hidden text-ellipsis text-sm')}
        >{`${t('integrations.common.create')} "${inputValue}"`}</span>
      </CommandItem>
    )
  }

  const EmptyItem = () => {
    if (!emptyIndicator) return undefined

    return <CommandEmpty>{emptyIndicator}</CommandEmpty>
  }

  // Leading content for an option: the enum mark when it has a color or icon, else its startIcon, else a
  // reserved slot when `reserve` is set
  const renderOptionMark = (option: Option<T> | undefined, reserve: boolean): ReactNode => {
    if (option && (option.color != null || option.iconName != null)) {
      return <EnumOptionMark color={option.color} iconName={option.iconName} />
    }
    if (option?.startIcon) {
      return option.startIcon
    }
    return reserve ? <EnumOptionMark reserveSpace /> : null
  }

  // The single-select trigger shows the selected option's mark; multi-select shows only its chips
  const triggerLeading = isMultiSelect
    ? startAdornment
    : (renderOptionMark(optionValues[0], false) ?? startAdornment)

  return (
    <InputWrapper
      errorText={errorText}
      helperText={helperText}
      label={label}
      required={required}
      showDirtyIndicator={showDirtyIndicator}
      tooltip={tooltip}
      wrapperClassName={wrapperClassName}
    >
      <div ref={anchorRef} className="relative">
        <Command
          ref={ref}
          className={cn('overflow-visible bg-white', className)}
          filter={(option, search) => {
            // If a value is selected, show all options
            if (
              (optionValues.length > 0 && !isMultiSelect) ||
              (isMultiSelect && inputValue === '')
            ) {
              return 1
            }
            if (option.toLowerCase().includes(search.toLowerCase())) {
              return 1
            }
            return 0
          }}
        >
          <div
            ref={scrollContainerRef}
            className={cn(
              getInputContainerClasses(disabled, undefined, isBorderless, readOnly),
              // Dropdown-specific overrides
              'px-3 min-h-9 rounded-sm py-2',
              disabled && 'cursor-not-allowed',
              horizontalScroll && 'flex-nowrap overflow-x-auto thin-scrollbar items-center',
              !horizontalScroll && !isMultiSelect && 'items-center',
              !horizontalScroll &&
                isMultiSelect &&
                optionValues?.length > 0 &&
                'flex-wrap items-start',
              // Less vertical padding when chips are present
              isMultiSelect && optionValues?.length > 0 && 'py-1.5',
            )}
            onClick={() => {
              if (!disabled && !readOnly) {
                setIsOpen(true)
                // Scroll to the end to show the input when clicking into the container
                if (scrollContainerRef.current && horizontalScroll) {
                  requestAnimationFrame(() => {
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollTo({
                        left: scrollContainerRef.current.scrollWidth,
                        behavior: 'smooth',
                      })
                    }
                  })
                }
              }
            }}
          >
            <div
              className={cn(
                'flex items-center gap-2 w-full min-w-0',
                horizontalScroll ? 'flex-nowrap' : 'flex-wrap',
              )}
            >
              {triggerLeading && (
                <div className="flex flex-shrink-0 items-center">{triggerLeading}</div>
              )}
              {isMultiSelect && (
                <div
                  className={cn(
                    'flex items-center gap-2',
                    horizontalScroll ? 'shrink-0 flex-nowrap' : 'flex-wrap',
                  )}
                >
                  {optionValues.map((value) => (
                    <EnumChip
                      key={value.value}
                      className={cn('h-6 py-0 text-sm', horizontalScroll && 'shrink-0')}
                      color={value.color}
                      iconName={value.iconName}
                      label={value.label}
                    />
                  ))}
                </div>
              )}
              <CommandPrimitive.Input
                {...inputProps}
                ref={inputRef}
                className={cn(
                  'flex-1 bg-transparent outline-none text-sm placeholder:text-gray-8 truncate',
                  !horizontalScroll && 'min-w-0 truncate',
                  horizontalScroll && 'shrink-0 min-w-[60px]',
                  disabled && 'cursor-not-allowed',
                  inputProps?.className,
                )}
                disabled={disabled}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onKeyDownCapture={handleKeyDownCapture}
                onValueChange={handleValueChange}
                placeholder={
                  optionValues.length === 0 || showPlaceholderWhenSelected ? placeholder : ''
                }
                value={inputValue}
                readOnly={readOnly || isAutocompleteDisabled}
              />
              {(optionValues[0]?.endIcon || endAdornment) && (
                <div className="flex flex-shrink-0 items-center">
                  {optionValues[0]?.endIcon || endAdornment}
                </div>
              )}
              {!disabled &&
              !readOnly &&
              showClearButton &&
              optionValues.length > 0 &&
              containerRef.current?.contains(document.activeElement) ? (
                <Button
                  type="button"
                  tabIndex={-1}
                  className="ms-2 h-4 flex-shrink-0 p-0"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setInputValue('')
                    clearSelection()
                    inputRef.current?.focus() // prevents focus loss which causes dropdown to become uncloseable
                  }}
                  size="xs"
                  variant="ghost"
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </Button>
              ) : (
                <Button
                  type="button"
                  tabIndex={-1}
                  className="ms-2 h-4 flex-shrink-0 p-0"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!disabled && !readOnly) {
                      if (isOpen) {
                        setIsOpen(false)
                        inputRef.current?.blur()
                      } else {
                        setIsOpen(true)
                        inputRef.current?.focus()
                      }
                    }
                  }}
                  size="xs"
                  variant="ghost"
                  disabled={disabled || readOnly}
                >
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-gray-10 flex-shrink-0 transition-transform duration-100',
                      disabled && 'text-gray-9',
                      isOpen && '-scale-y-100',
                    )}
                  />
                </Button>
              )}
            </div>
          </div>
          {isOpen &&
            (() => {
              const dropdownContent = (
                <div
                  ref={floatingRef}
                  className={cn(
                    'z-[9999] border rounded-sm bg-popover text-popover-foreground shadow-md outline-none animate-in my-1',
                  )}
                  style={{ ...floatingStyles, minWidth: containerRef.current?.offsetWidth }}
                >
                  <CommandList>
                    {EmptyItem()}
                    {CreatableItem()}
                    {options?.length > 0 ? (
                      options?.map((option) => {
                        const isSelected =
                          optionValues?.some((value) => value.value === option.value) || false
                        const optionMark = renderOptionMark(option, someOptionHasIcon)
                        return (
                          <CommandItem
                            key={option.value}
                            className={cn(
                              'cursor-pointer text-gray-10 hover:text-gray-12 text-xs flex items-center',
                              option.disable && 'cursor-not-allowed text-gray-10',
                              isSelected && "bg-gray-2 aria-[selected='true']:bg-gray-3",
                            )}
                            disabled={option.disable}
                            onMouseDown={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                            }}
                            onMouseUpCapture={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                            }}
                            onSelect={() => {
                              if (isSelected && isMultiSelect) {
                                handleUnselect(option)
                              } else {
                                if (isMultiSelect) {
                                  onChangeMulti?.(
                                    [...optionValues, option].map((value) => value.value),
                                  )
                                } else {
                                  onChangeSingle?.(option.value)
                                  setIsOpen(false)
                                  if (blurOnSelect) {
                                    inputRef.current?.blur()
                                  }
                                }
                                // Clear the search text. The useEffect syncing optionValues → inputValue
                                // will restore the selected label on the next render for single-select.
                                setInputValue('')
                              }
                            }}
                            value={option.label}
                          >
                            <Check
                              className={cn(
                                `me-2 h-3.5 w-3.5 flex-shrink-0 text-gray-9`,
                                isSelected ? 'visible' : 'invisible',
                              )}
                            />
                            {optionMark && (
                              <span className="me-2 flex items-center">{optionMark}</span>
                            )}
                            {option.description ? (
                              <span className="flex flex-col items-start">
                                <span>{option.label}</span>
                                <span className="text-[10px] text-gray-8">
                                  {option.description}
                                </span>
                              </span>
                            ) : (
                              option.label
                            )}
                            {option.endIcon && (
                              <span className="ms-2 flex flex-shrink-0 items-center">
                                {option.endIcon}
                              </span>
                            )}
                          </CommandItem>
                        )
                      })
                    ) : (
                      <CommandEmpty>{emptyIndicator}</CommandEmpty>
                    )}
                  </CommandList>
                </div>
              )

              return usePortal ? (
                <FloatingPortal>{dropdownContent}</FloatingPortal>
              ) : (
                dropdownContent
              )
            })()}
        </Command>
      </div>
    </InputWrapper>
  )
}

// Export the generic component
export const Dropdown = forwardRef(DropdownComponent) as <T extends string = string>(
  props: DropdownProps<T> & { ref?: Ref<HTMLInputElement> },
) => JSX.Element

// Set displayName on the component
;(Dropdown as any).displayName = 'Dropdown'
